import { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, Trash2, RefreshCw, FileText, Building2, Receipt, CheckCircle2, Clock } from 'lucide-react';

export default function PurchaseBook() {
    const { API } = useContext(AuthContext);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        loadEntries();
    }, []);

    const loadEntries = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/sii/purchase-book?limit=100`);
            setEntries(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            toast.error('Error al cargar libro de compras');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.name.endsWith('.csv')) {
            toast.error('Por favor seleccione un archivo CSV');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post(`${API}/sii/purchase-book/import`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const result = response.data;
            if (result.duplicates > 0) {
                toast.success(`${result.imported} facturas importadas, ${result.duplicates} duplicados omitidos`);
            } else {
                toast.success(`${result.imported} facturas importadas`);
            }
            loadEntries();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al importar archivo');
            console.error(error);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Eliminar esta entrada?')) return;
        try {
            await axios.delete(`${API}/sii/purchase-book/${id}`);
            toast.success('Entrada eliminada');
            loadEntries();
        } catch (error) {
            toast.error('Error al eliminar');
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    const stats = {
        total: entries.length,
        pending: entries.filter(e => e.status === 'pending').length,
        matched: entries.filter(e => e.status === 'matched').length,
        totalAmount: entries.reduce((sum, e) => sum + (e.monto_total || 0), 0)
    };

    return (
        <div className="p-8 space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-2">
                        <Receipt className="h-8 w-8 text-primary" />
                        Libro de Compras (SII)
                    </h1>
                    <p className="text-muted-foreground">Importa y gestiona tus facturas de compra desde el SII.</p>
                </div>
                <Button variant="outline" onClick={loadEntries} disabled={loading}>
                    <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Recargar
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="text-2xl font-bold">{stats.total}</div>
                        <p className="text-muted-foreground text-sm">Facturas totales</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6 flex items-center gap-2">
                        <Clock className="h-5 w-5 text-yellow-500" />
                        <div>
                            <div className="text-2xl font-bold">{stats.pending}</div>
                            <p className="text-muted-foreground text-sm">Por conciliar</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6 flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                        <div>
                            <div className="text-2xl font-bold">{stats.matched}</div>
                            <p className="text-muted-foreground text-sm">Conciliadas</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="text-2xl font-bold">{formatCurrency(stats.totalAmount)}</div>
                        <p className="text-muted-foreground text-sm">Monto total</p>
                    </CardContent>
                </Card>
            </div>

            {/* Upload Section */}
            <Card className="border-dashed">
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Upload className="h-5 w-5" />
                        Importar Libro de Compras SII
                    </CardTitle>
                    <CardDescription>
                        Sube el archivo CSV descargado desde el portal del SII.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex gap-4 items-end">
                        <div className="flex-1">
                            <Label>Archivo CSV del SII</Label>
                            <Input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv"
                                onChange={handleFileUpload}
                                disabled={uploading}
                                className="mt-1"
                            />
                        </div>
                        {uploading && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <RefreshCw className="h-4 w-4 animate-spin" />
                                Procesando...
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Entries Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Facturas de Compra
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="py-8 text-center text-muted-foreground">
                            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                            Cargando...
                        </div>
                    ) : entries.length === 0 ? (
                        <div className="py-12 text-center text-muted-foreground">
                            <Receipt className="h-12 w-12 mx-auto mb-4 opacity-30" />
                            <p>No hay facturas de compra registradas.</p>
                            <p className="text-sm">Importa un archivo CSV del SII para comenzar.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left p-2">Fecha</th>
                                        <th className="text-left p-2">RUT</th>
                                        <th className="text-left p-2">Razón Social</th>
                                        <th className="text-right p-2">Folio</th>
                                        <th className="text-right p-2">Neto</th>
                                        <th className="text-right p-2">IVA</th>
                                        <th className="text-right p-2">Total</th>
                                        <th className="text-center p-2">Estado</th>
                                        <th className="text-right p-2">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map((entry) => (
                                        <tr key={entry.id} className="border-b hover:bg-secondary/30">
                                            <td className="p-2 font-mono text-xs">{entry.fecha_emision}</td>
                                            <td className="p-2 font-mono text-xs">{entry.rut_emisor}</td>
                                            <td className="p-2 max-w-[200px] truncate" title={entry.razon_social}>
                                                {entry.razon_social}
                                            </td>
                                            <td className="p-2 text-right font-mono">{entry.folio}</td>
                                            <td className="p-2 text-right font-mono">
                                                {formatCurrency(entry.monto_neto)}
                                            </td>
                                            <td className="p-2 text-right font-mono text-blue-600">
                                                {formatCurrency(entry.iva)}
                                            </td>
                                            <td className="p-2 text-right font-mono font-medium">
                                                {formatCurrency(entry.monto_total)}
                                            </td>
                                            <td className="p-2 text-center">
                                                <span className={`px-2 py-0.5 rounded-full text-xs ${entry.status === 'matched'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {entry.status === 'matched' ? 'Conciliada' : 'Pendiente'}
                                                </span>
                                            </td>
                                            <td className="p-2 text-right">
                                                <Button size="sm" variant="ghost" onClick={() => handleDelete(entry.id)}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
